import { Type } from '@angular/core';
import { NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { EMPTY, Observable, of, OperatorFunction } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { handleAddEditParams, IAddParams, IEditParams } from './route-helper';

// URL params are strings; the entities carry a string `id`. Structural so the operator
// stays usable for any row type without a shared base interface.
const hasId = (entity: unknown, id: string): boolean =>
  typeof entity === 'object' && !!entity && (entity as { id?: unknown }).id === id;

// `NzDrawerService.create` constrains its component type to `{}` (ng-zorro 15+), so the
// constraint has to be carried through every generic that feeds into it.
// `NonNullable<unknown>` is that same type, spelled the way `ban-types` allows.
interface IOptions<TDrawerComponent extends NonNullable<unknown>, TEntity> {
  drawerService: NzDrawerService;
  componentType: Type<TDrawerComponent>;

  /** Human readable name of the entity, used in the drawer title. */
  name: string;

  /**
   * The rows currently on screen. Checked before `getSingle`, because every edit is
   * launched from a visible row — this is what the Akita version got from reading the
   * entity store, which only the paginator ever wrote.
   */
  loadedEntities?: readonly TEntity[];

  /** Fetches the entity when it is not among `loadedEntities`. */
  getSingle(id: string): Observable<TEntity>;
  notification: NzNotificationService;

  /** Extra `@Input()` params passed to the drawer when opened. */
  extraParams?: Partial<TDrawerComponent>;
}

const openDrawer = <TDrawerComponent extends NonNullable<unknown>, TEntity>(
  { drawerService, componentType, name, extraParams }: IOptions<TDrawerComponent, TEntity>,
  entity?: TEntity
): NzDrawerRef<TDrawerComponent, boolean | undefined> => {
  if (entity) {
    // `editValue` is the edit drawer's contract, which nothing types yet, so it rides in
    // as the drawer's data type argument.
    // TODO should be inferred from IEditDrawerComponent
    //
    // The assertion stays: TypeScript rejects an object literal assigned to a `Partial<>`
    // of an unresolved generic, which is what `nzContentParams: Partial<T & D>` is here.
    //
    // `editValue` goes last: it is the entity this drawer was opened to edit, and
    // `Partial<TDrawerComponent>` permits an `editValue` key, so spreading after it could
    // silently replace the fetched entity — including with `undefined`.
    const contentParams = {
      ...extraParams,
      editValue: entity,
    } as Partial<TDrawerComponent & { editValue: TEntity }>;

    return drawerService.create<TDrawerComponent, { editValue: TEntity }, boolean | undefined>({
      nzContent: componentType,
      nzContentParams: contentParams,
      nzTitle: `Edit ${name}`,
      nzWidth: 520,
    });
  }

  return drawerService.create<TDrawerComponent, undefined, boolean | undefined>({
    nzContent: componentType,
    nzContentParams: extraParams,
    nzTitle: `Create a new ${name}`,
    nzWidth: 520,
  });
};

/**
 * Opens a create or edit drawer based on route parameters:
 * - `/foo?mode=add` opens a create drawer
 * - `/foo?mode=edit&id=1` fetches entity 1 and opens an edit drawer
 *
 * Emits the drawer's close result.
 */
export const createOrEditFromParams = <
  TDrawerComponent extends NonNullable<unknown>,
  TEntity,
  TInput,
>(
  optionsCallback: (input?: TInput) => IOptions<TDrawerComponent, TEntity>
): OperatorFunction<
  [IAddParams | IEditParams, TInput] | [IAddParams | IEditParams],
  boolean | undefined
> =>
  switchMap(([params, input]) => {
    const options = input ? optionsCallback(input) : optionsCallback();

    const afterClose$ = handleAddEditParams(
      params,
      () => openDrawer(options).afterClose,
      (id) => {
        const loaded = options.loadedEntities?.find((entity) => hasId(entity, id));

        return (loaded ? of(loaded) : options.getSingle(id)).pipe(
          catchError(() => {
            options.notification.error('Error', `Unable to find ${options.name} with id: ${id}`);

            return EMPTY;
          }),
          switchMap((entity) => openDrawer(options, entity).afterClose)
        );
      }
    );

    return afterClose$ ?? EMPTY;
  });
